// ShiftEndModal — count the drawer, write the handover note, review open items → end shift (handover pending).
import React, { useEffect, useState } from 'react';
import { Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { apiCall } from './_shared';
import { VoiceNoteRecorder } from './VoiceNote';

const eur = (v) => `€${Number(v || 0).toFixed(2)}`;

export const OpenItemsSummary = ({ items }) => {
  const c = items?.counts || {};
  const chips = [
    ['Arrivals not in', c.arrivals_pending, 'bg-orange-100 text-orange-800'], ['Departures not out', c.departures_pending, 'bg-sky-100 text-sky-800'],
    [`Unpaid folios · ${eur(c.unpaid_total)}`, c.unpaid_folios, 'bg-red-100 text-red-800'], ['Open tickets', c.open_tickets, 'bg-violet-100 text-violet-800'], ['Chats waiting', c.chats_waiting, 'bg-emerald-100 text-emerald-800'],
  ];
  return (
    <div className="flex flex-wrap gap-2" data-testid="shift-open-items">
      {chips.map(([label, n, cls]) => <span key={label} className={`text-xs font-semibold rounded-full px-2.5 py-1 ${n ? cls : 'bg-slate-100 text-slate-500'}`}>{n || 0} · {label}</span>)}
      {items?.board_error && <span className="text-xs text-amber-700">Beds24 board unavailable</span>}
    </div>
  );
};

const ShiftEndModal = ({ shift, onClose, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [counted, setCounted] = useState('');
  const [note, setNote] = useState('');
  const [voice, setVoice] = useState(shift.voice_note || null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    apiCall(`/api/admin/shifts/${shift.id}/end-preview`).then((p) => { setPreview(p); setCounted(String(p.cash.expected)); }).catch((e) => setErr(e.message));
  }, [shift.id]);

  const c = Number(counted);
  const diff = preview ? Math.round((c - preview.cash.expected) * 100) / 100 : 0;
  const undone = preview ? preview.template.filter((t) => !preview.checklist?.[t.key]?.done) : [];

  const submit = async () => {
    if (!(c >= 0)) { setErr('Enter the counted cash'); return; }
    if (undone.length && !window.confirm(`${undone.length} checklist item(s) are not done. End the shift anyway?`)) return;
    setBusy(true); setErr('');
    try { setResult(await apiCall(`/api/admin/shifts/${shift.id}/end`, { method: 'POST', body: JSON.stringify({ counted_cash: c, handover_note: note }) })); onDone?.(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="shift-end-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5 space-y-4">
        <h3 className="text-lg font-bold text-slate-900">End shift & hand over</h3>
        {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="shift-end-error">{err}</div>}
        {!preview && !err && <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Collecting cash and open items…</div>}
        {preview && !result && (
          <>
            <div className="grid sm:grid-cols-4 gap-2 text-sm">
              {[['Opening float', preview.cash.opening], ['Cash received', preview.cash.cash_in], ['Withdrawn', preview.cash.withdrawn], ['Expected in drawer', preview.cash.expected]].map(([l, v]) => (
                <div key={l} className="border rounded-lg p-2"><div className="text-[11px] uppercase text-slate-500">{l}</div><div className="font-bold">{eur(v)}</div></div>
              ))}
            </div>
            <label className="block text-sm">Counted cash (€)
              <input type="number" step="0.01" min="0" value={counted} onChange={(e) => setCounted(e.target.value)} className="mt-1 border rounded-lg px-3 py-2 w-full text-lg font-semibold" data-testid="shift-end-counted" />
            </label>
            {preview && counted !== '' && <div className={`text-sm font-semibold ${Math.abs(diff) > 0.009 ? 'text-red-700' : 'text-emerald-700'}`} data-testid="shift-end-diff">{Math.abs(diff) > 0.009 ? `Discrepancy ${diff > 0 ? '+' : ''}${eur(diff)}` : 'Drawer matches'}</div>}
            <div><div className="text-xs uppercase text-slate-500 mb-1">Open items handed over (captured automatically)</div><OpenItemsSummary items={preview.open_items} /></div>
            {undone.length > 0 && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0" /> Not done: {undone.map((u) => u.label).join(' · ')}</div>}
            <label className="block text-sm">Handover note for the next shift
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder="Late arrival room 716 (ETA 01:30), guest in 903 asked for extra towels, pool pump noise reported…" className="mt-1 border rounded-lg px-3 py-2 w-full" data-testid="shift-end-note" />
            </label>
            <div className="text-sm">
              <div className="text-slate-700 mb-1">Voice handover (optional) — 30 s, transcribed automatically and attached to the report</div>
              <VoiceNoteRecorder shiftId={shift.id} note={voice} onChange={setVoice} />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border text-sm" data-testid="shift-end-cancel">Cancel</button>
              <button type="button" onClick={submit} disabled={busy} className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold disabled:opacity-60" data-testid="shift-end-submit">{busy ? 'Ending…' : 'End shift'}</button>
            </div>
          </>
        )}
        {result && (
          <div className="space-y-3" data-testid="shift-end-result">
            <div className="flex items-center gap-2 text-emerald-700 font-semibold"><CheckCircle2 className="w-5 h-5" /> Shift ended — handover is waiting for the next receptionist</div>
            {result.alerts?.includes('discrepancy') && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">Cash discrepancy {eur(result.shift.cash.discrepancy)} — the manager has been notified.</div>}
            <div className="text-xs text-slate-500">{result.shift.mail?.sent ? `Handover report e-mailed to ${result.shift.mail.to}.` : result.shift.mail?.error ? `Report e-mail not sent: ${result.shift.mail.error}` : 'No manager e-mail configured — report is available in the shift history.'}</div>
            <div className="flex justify-end gap-2">
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/shifts/${shift.id}/report.html`} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-lg border text-sm" data-testid="shift-end-report">Open report</a>
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold" data-testid="shift-end-close">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShiftEndModal;
